import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Loader2, Layers, Pencil, Trash2, ChevronRight, CheckCircle, UserPlus, X, Check } from 'lucide-react'
import type { Season, BracketTemplate, SeasonBracket, RegistrationWithProfile } from './types'
import { formatDate, getStatusLabel } from './utils'

interface SeasonBracketsViewProps {
  season: Season
  templates: BracketTemplate[]
  seasonBrackets: SeasonBracket[]
  // Template/bracket state
  isTemplateActive: (templateId: string) => boolean
  togglingBracket: string | null
  onToggleBracket: (template: BracketTemplate, enable: boolean) => void
  onBracketClick: (bracketId: string) => void
  // Navigation
  onBackToSeasons: () => void
  onEditSeason: () => void
  onDeleteSeason: () => void
  deletingId: string | null
  // Signups
  togglingSignups: string | null
  onToggleSignups: () => void
  // Registrations
  registrations: RegistrationWithProfile[]
  registrationsLoading: boolean
  processingRegistration: string | null
  onApproveRegistration: (registration: RegistrationWithProfile) => void
  onRejectRegistration: (registration: RegistrationWithProfile) => void
  // Animation
  actionsVisible: boolean
}

export function SeasonBracketsView({
  season,
  templates,
  seasonBrackets,
  isTemplateActive,
  togglingBracket,
  onToggleBracket,
  onBracketClick,
  onBackToSeasons,
  onEditSeason,
  onDeleteSeason,
  deletingId,
  togglingSignups,
  onToggleSignups,
  registrations,
  registrationsLoading,
  processingRegistration,
  onApproveRegistration,
  onRejectRegistration,
  actionsVisible,
}: SeasonBracketsViewProps) {
  const pendingRegistrations = registrations.filter(r => r.status === 'pending')
  const confirmedCount = registrations.filter(r => r.status === 'confirmed').length
  const rejectedCount = registrations.filter(r => r.status === 'rejected').length

  return (
    <>
      <Card>
        <CardHeader className="pb-4">
          {/* Breadcrumb */}
          <div className="flex items-center gap-1.5 text-sm text-muted-foreground mb-2">
            <button onClick={onBackToSeasons} className="hover:text-foreground hover:underline">
              Seasons
            </button>
            <ChevronRight className="h-3.5 w-3.5" />
            <span className="text-foreground font-medium truncate">{season.name}</span>
          </div>
          <div className="flex items-center justify-between gap-4">
            <div className="min-w-0">
              <CardTitle className="flex items-center gap-2">
                <Layers className="h-5 w-5 shrink-0" />
                <span className="truncate">Brackets</span>
                <span className={`text-xs font-normal px-2 py-0.5 rounded-full ${
                  season.status === 'active' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                  season.status === 'upcoming' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' :
                  'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
                }`}>
                  {getStatusLabel(season.status)}
                </span>
              </CardTitle>
              <CardDescription>
                {formatDate(season.startDate)} – {formatDate(season.endDate)}
              </CardDescription>
            </div>
            {/* Actions slide in from right on page transition */}
            <div className={`flex items-center gap-2 shrink-0 transition-all duration-300 ease-out ${
              actionsVisible 
                ? 'translate-x-0 opacity-100' 
                : 'translate-x-8 opacity-0'
            }`}>
              {/* Signups toggle for upcoming seasons */}
              {season.status === 'upcoming' && (
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-muted/50">
                  <span className="text-sm text-muted-foreground">Signups</span>
                  {togglingSignups === season.seasonId ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Switch
                      checked={season.signupsOpen}
                      onCheckedChange={onToggleSignups}
                      className="data-[state=checked]:bg-emerald-600"
                    />
                  )}
                </div>
              )}
              <Button variant="outline" size="sm" onClick={onEditSeason}>
                <Pencil className="h-4 w-4 mr-1" />
                Edit
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                className="text-destructive hover:text-destructive"
                disabled={deletingId === season.seasonId} 
                onClick={onDeleteSeason}
              >
                {deletingId === season.seasonId ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <Trash2 className="h-4 w-4 mr-1" />
                    Delete
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {templates.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No bracket types defined. Create some in the sidebar.
            </div>
          ) : (
            <div className="space-y-2">
              {templates.map(template => {
                const isActive = isTemplateActive(template.templateId)
                const bracket = seasonBrackets.find(b => b.templateId === template.templateId)
                const isToggling = togglingBracket === template.templateId
                
                return (
                  <div
                    key={template.templateId}
                    className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                      isActive ? 'bg-primary/5 border-primary/20' : 'hover:bg-muted/50'
                    } ${isActive ? 'cursor-pointer' : ''}`}
                    onClick={() => {
                      if (isActive && bracket) {
                        onBracketClick(bracket.bracketId)
                      }
                    }}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        {isActive && <CheckCircle className="h-4 w-4 text-primary" />}
                        <span className={isActive ? 'font-medium' : ''}>{template.name}</span>
                      </div>
                      {template.description && (
                        <p className="text-sm text-muted-foreground">{template.description}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                      {isToggling && <Loader2 className="h-4 w-4 animate-spin" />}
                      <Switch
                        checked={isActive}
                        disabled={isToggling}
                        onCheckedChange={checked => onToggleBracket(template, checked)}
                      />
                    </div>
                    {isActive && <ChevronRight className="h-4 w-4 text-muted-foreground" />}
                  </div>
                )
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pending Registrations - show for upcoming seasons with signups open */}
      {season.status === 'upcoming' && season.signupsOpen && (
        <Card className="mt-4">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base flex items-center gap-2">
                  <UserPlus className="h-4 w-4" />
                  Pending Registrations
                </CardTitle>
                <CardDescription>
                  {pendingRegistrations.length} awaiting approval
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {registrationsLoading ? (
              <div className="flex justify-center py-4">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : pendingRegistrations.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">
                No pending registrations
              </p>
            ) : (
              <div className="space-y-2">
                {pendingRegistrations.map(registration => (
                  <div
                    key={registration.registrationId}
                    className="flex items-center gap-3 p-3 rounded-lg border bg-muted/30"
                  >
                    <Avatar className="h-9 w-9">
                      <AvatarImage src={registration.profile.picture} />
                      <AvatarFallback>
                        {registration.profile.displayName.charAt(0).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{registration.profile.displayName}</p>
                      <p className="text-xs text-muted-foreground">
                        Wants to join: {registration.preferredBracketName || 'Any bracket'}
                      </p>
                    </div>
                    <div className="flex items-center gap-1">
                      {processingRegistration === registration.registrationId ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 text-destructive hover:text-destructive"
                            onClick={() => onRejectRegistration(registration)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            className="h-8 bg-emerald-600 hover:bg-emerald-700"
                            onClick={() => onApproveRegistration(registration)}
                          >
                            <Check className="h-4 w-4 mr-1" />
                            Approve
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Show confirmed/rejected counts if any */}
            {(confirmedCount > 0 || rejectedCount > 0) && (
              <div className="mt-4 pt-4 border-t text-sm text-muted-foreground">
                <p>
                  {confirmedCount} confirmed, {rejectedCount} rejected
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </>
  )
}
